import { useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { copyToClipboard } from '../../../lib/utils';
import { AppContext, SttTable, SttTopControls } from '../../../sporttia/all';
import ActivityRateDialog from './ActivityRateDialog';
import { PERMISSIONS } from '../../../lib/permissions';

type Props = {
    item: Activity;
    onSave: (id?: number) => void;
};

type Activity = {
    id: number;
    ini?: string;
    end?: string;
    rates?: Rate[];
};

type Rate = {
    id: number;
    name: string;
    ini: string;
    end: string;
    enrollment: number;
    fee: number;
    period: string;
    numStudents: number;
    maxStudents: number | null;
    signupOnline: boolean;
    mandatory?: {
        name: string;
    };
};

export default function ActivityRates({
    item,
    onSave,
}: Props): React.ReactElement {
    const cxt = useContext(AppContext)!;
    const { t } = useTranslation();

    const [rate, setRate] = useState<Rate | Partial<Rate> | null>(null);

    const rates = item.rates
        ? { count: item.rates.length, rows: item.rates }
        : { rows: [], count: 0 };

    return (
        <>
            <SttTopControls
                mb={6}
                mainAction={{
                    type: 'create',
                    permission: PERMISSIONS.ACTIVITY.MODIFY,
                    onClick: () =>
                        setRate({
                            ini: item.ini,
                            end: item.end,
                        }),
                }}
            />
            <SttTable
                autoload={false}
                hidePagination
                data={rates}
                columns={[
                    {
                        title: '#',
                        type: 'tooltip',
                        value: (row: Rate) => ({ label: '#', value: row.id }),
                        onClick: (row: Rate) => {
                            copyToClipboard(row.id.toString(), () =>
                                cxt?.showMessage('S', t('copiedToClipboard')),
                            );
                        },
                    },
                    {
                        type: 'link',
                        title: t('Name'),
                        field: 'name',
                        align: 'left',
                        onClick: (row: Rate) => setRate(row),
                    },
                    {
                        title: t('Period'),
                        type: 'period',
                        value: (row: Rate) => ({ ini: row.ini, end: row.end }),
                    },
                    {
                        title: t('Enrollment'),
                        align: 'right',
                        type: 'price',
                        field: 'enrollment',
                    },
                    {
                        title: t('Fees'),
                        type: 'enrollment',
                        value: (row: Rate) => ({
                            fee: row.fee,
                            interval: row.period,
                        }),
                    },
                    {
                        title: t('Students'),
                        type: 'text',
                        value: (row: Rate) =>
                            `${row.numStudents} / ${row.maxStudents || '∞'}`,
                    },
                    {
                        title: t('SignupOnline'),
                        type: 'active',
                        field: 'signupOnline',
                    },
                    {
                        title: t('Mandatory'),
                        type: 'text',
                        value: (row: Rate) =>
                            row.mandatory ? row.mandatory.name : null,
                    },
                ]}
            />

            {rate !== null && (
                <ActivityRateDialog
                    open
                    activityId={item.id}
                    item={rate as any}
                    onClose={() => {
                        setRate(null);
                    }}
                    onSave={onSave}
                    deleteRateEnabled={
                        !(
                            rates.count <= 1 ||
                            ((rate as Rate).numStudents ?? 0) >= 1
                        )
                    }
                />
            )}
        </>
    );
}
